/**
 * Organization Table Component
 */

import React from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import type { Organization } from "../../../store/api/organizationsApi";
import { formatDateTime } from "../../../utils/formatDate";

interface OrganizationTableProps {
	organizations: Organization[];
	onEdit: (organization: Organization) => void;
	onDelete: (id: string, name: string) => void;
	isDeleting: boolean;
	currentPage: number;
	totalCount: number;
	pageSize: number;
	onPageChange: (page: number) => void;
}

const OrganizationTable: React.FC<OrganizationTableProps> = ({
	organizations,
	onEdit,
	onDelete,
	isDeleting,
	currentPage,
	totalCount,
	pageSize,
	onPageChange,
}) => {
	const totalPages = Math.ceil(totalCount / pageSize);

	const getStatusBadge = (status: string) => {
		const baseClasses = "px-2 py-1 text-xs font-medium rounded-full capitalize";
		switch (status) {
			case "active":
				return `${baseClasses} bg-success/10 text-success`;
			case "inactive":
				return `${baseClasses} bg-muted text-muted-foreground`;
			case "suspended":
				return `${baseClasses} bg-destructive/10 text-destructive`;
			default:
				return `${baseClasses} bg-muted text-muted-foreground`;
		}
	};

	if (organizations.length === 0) {
		return (
			<div className="bg-card border border-border rounded-lg p-12 text-center">
				<Icon
					name="Building2"
					size={48}
					className="mx-auto mb-4 text-muted-foreground"
				/>
				<h3 className="text-lg font-semibold text-foreground mb-2">
					No organizations found
				</h3>
				<p className="text-muted-foreground">
					Get started by creating your first organization.
				</p>
			</div>
		);
	}

	return (
		<div className="bg-card border border-border rounded-lg overflow-hidden">
			<div className="overflow-x-auto -mx-6 sm:mx-0">
				<div className="inline-block min-w-full align-middle sm:px-0">
					<table className="min-w-full divide-y divide-border">
					<thead className="bg-muted/50 border-b border-border">
						<tr>
							<th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
								Name
							</th>
							<th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
								Contact
							</th>
							<th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
								Location
							</th>
							<th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
								Status
							</th>
							<th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
								Created
							</th>
							<th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
								Actions
							</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-border">
						{organizations.map((org) => (
							<tr key={org.id} className="hover:bg-muted/30 transition-colors">
								<td className="px-6 py-4 whitespace-nowrap">
									<div className="text-sm font-medium text-foreground">
										{org.name}
									</div>
									{org.email && (
										<div className="text-sm text-muted-foreground">
											{org.email}
										</div>
									)}
								</td>
								<td className="px-6 py-4 whitespace-nowrap">
									<div className="text-sm text-foreground">
										{org.phone || "N/A"}
									</div>
								</td>
								<td className="px-6 py-4 whitespace-nowrap">
									<div className="text-sm text-foreground">
										{org.city && org.state
											? `${org.city}, ${org.state}`
											: org.city || org.state || "N/A"}
									</div>
								</td>
								<td className="px-6 py-4 whitespace-nowrap">
									<span className={getStatusBadge(org.status)}>
										{org.status}
									</span>
								</td>
								<td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
									{formatDateTime(org.created_at)}
								</td>
								<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
									<div className="flex items-center justify-end gap-2">
										<Button
											variant="ghost"
											size="sm"
											onClick={() => onEdit(org)}
											iconName="Edit"
											className="text-primary hover:text-primary"
										>
											Edit
										</Button>
										<Button
											variant="ghost"
											size="sm"
											onClick={() => onDelete(org.id, org.name)}
											disabled={isDeleting}
											iconName="Trash2"
											className="text-destructive hover:text-destructive"
										>
											Delete
										</Button>
									</div>
								</td>
							</tr>
						))}
					</tbody>
				</table>
				</div>
			</div>

			{/* Pagination */}
			{totalPages > 1 && (
				<div className="px-6 py-4 border-t border-border flex items-center justify-between">
					<div className="text-sm text-muted-foreground">
						Showing {(currentPage - 1) * pageSize + 1} to{" "}
						{Math.min(currentPage * pageSize, totalCount)} of {totalCount}{" "}
						organizations
					</div>
					<div className="flex items-center gap-2">
						<Button
							variant="outline"
							size="sm"
							onClick={() => onPageChange(currentPage - 1)}
							disabled={currentPage === 1}
							iconName="ChevronLeft"
						>
							Previous
						</Button>
						<span className="text-sm text-foreground">
							Page {currentPage} of {totalPages}
						</span>
						<Button
							variant="outline"
							size="sm"
							onClick={() => onPageChange(currentPage + 1)}
							disabled={currentPage === totalPages}
							iconName="ChevronRight"
						>
							Next
						</Button>
					</div>
				</div>
			)}
		</div>
	);
};

export default OrganizationTable;
